// src/components/SignUpPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  CircularProgress,
  FormLabel,
  Modal,
  Checkbox,
  Snackbar,
  Alert
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { useAuth } from "../context/AuthProvider";
// import cloudImage from "../img/image-log-reg.jpg";
import cloudImage from "../img/athrv-cloudimage-5.png";
import athrvLogo from "../img/athrv-cloud-logo-1.png";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
const CustomTextField = styled(TextField)(({ theme }) => ({
  // When the label has not shrunk (data-shrink="false"), hide the input text
  '& .MuiInputLabel-root[data-shrink="false"] ~ .MuiInputBase-root input': {
    opacity: 0,
    transition: 'opacity 0.3s ease', // optional smooth transition
  },
  // When the label is shrunk (data-shrink="true"), show the input text
  '& .MuiInputLabel-root[data-shrink="true"] ~ .MuiInputBase-root input': {
    opacity: 1,
    transition: 'opacity 0.3s ease',
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
    WebkitBoxShadow: '0 0 0 1000px #e8f0fe inset !important',
    transition: 'background-color 5000s ease-in-out 0s',
  },
}));

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 800,
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflowY: 'auto',
};

const signupValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required")
    .max(254)
    .test("includes-dot", "Email must contain at least one dot (.)", (val) =>
      val ? val.includes(".") : false
    ),

  password: Yup.string()
    .required("Password is required")
    .min(6)
    .max(128)
    .matches(/[a-z]/, "Must contain at least one lowercase")
    .matches(/[A-Z]/, "Must contain at least one uppercase")
    .matches(/[0-9]/, "Must contain at least one number")
    .matches(/[^a-zA-Z0-9\s]/, "Must contain exactly one special character")
    .test(
      "exact-special-char",
      "Must contain exactly one special character",
      (value) => {
        if (!value) return false;
        const special = value.replace(/[a-zA-Z0-9]/g, "");
        return special.length === 1;
      }
    ),

  user_phone: Yup.string()
    .required("Phone number is required")
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits"),

  organization_name: Yup.string().when("userType", {
    is: "organization",
    then: Yup.string().required("Organization name is required"),
  }),

  contact_email: Yup.string().when("userType", {
    is: "organization",
    then: Yup.string()
      .email("Enter a valid contact email")
      .required("Contact email is required")
      .max(254)
      .test("includes-dot", "Must contain at least one dot (.)", (val) =>
        val ? val.includes(".") : false
      ),
  }),

  phone: Yup.string().when("userType", {
    is: "organization",
    then: Yup.string()
      .required("Organization phone is required")
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits"),
  }),
});
const FormikTextField = ({ field, form: { touched, errors }, ...props }) => {
  const hasError = touched[field.name] && errors[field.name];
  return (
    <Box sx={{ mb: 2 }}>
      <CustomTextField
        {...field}
        {...props}
        fullWidth
        variant="outlined"
        error={hasError}
        helperText={hasError ? errors[field.name] : ""}
      />
    </Box>
  );
};

const LogoHeader = () => {
  return (
    <Box
      component="img"
      src={athrvLogo}
      alt="Athrv Cloud"
      sx={{
        display: "block",
        mx: "auto", // shorthand for marginLeft and marginRight auto
        mb: 3,
        maxWidth: 150, // adjust to your desired max width
        height: "auto",
      }}
    />
  );
};

const initialValues = {
  userType: "",
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  user_phone: "",
  organization_name: "",
  organization_type: "",
  address: "",
  contact_email: "",
  phone: "",
};

const SignUpPage = () => {
  // Form Fields
  const [userType, setUserType] = useState("individual");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user_phone, setUser_phone] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [organization_name, setOrganization_name] = useState("");
  const [address, setAddress] = useState("");
  const [contact_email, setContact_email] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  //const [error, setError] = useState(null);
  //const [success, setSuccess] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [openPrivacyModal, setOpenPrivacyModal] = useState(false);
  const [termsContent, setTermsContent] = useState("");
  const [privacyContent, setPrivacyContent] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/terms-and-condtions`);
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          setTermsContent(data.data[0].terms_and_condition_content);
          setPrivacyContent(data.data[0].privacy_policy_content);
        }
      } catch (error) {
        console.error("Failed to fetch terms and conditions:", error);
      }
    };
    fetchTerms();
  }, []);

  const handleSnackbarClose = (event, reason) => {
  if (reason === 'clickaway') {
    return;
  }
  setSnackbarOpen(false);
};

  const handleSendOtp = async (values) => {
  setIsLoading(true);
  const {
    firstName,
    lastName,
    email,
    password,
    user_phone,
    organization_name,
    address,
    contact_email,
    phone,
  } = values;

  const requestData = {
    fname: firstName,
    lname: lastName,
    email: email,
    password: password,
    user_phone: user_phone,
    organization_name: userType === "individual" ? `${firstName} ${lastName}` : organization_name,
    organization_type: userType === "individual" ? "I" : "O",
    address: address,
    contact_email: userType === "individual" ? email : contact_email,
    phone: userType === "individual" ? user_phone : phone,
  };

  try {
    console.log("Sending request to backend...");
    const response = await fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/auth/register`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      }
    );
    console.log("Response status:", response.status);
    //console.log("Response ok?:", response.ok);
    let data;
    try {
      data = await response.json();
      console.log("Backend response data:", data);
    } catch (jsonError) {
      console.error("Failed to parse JSON:", jsonError);
      setSnackbarMessage("Server error. Please try again later.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsLoading(false);
      throw new Error("Server error");
      //return false;
    }

    //const data = await response.json();
    //console.log("backend response:", data); 
    
    if (!response.ok) {
      console.log("=== ERROR RESPONSE FROM BACKEND ===");
      console.log("Full response data:", data);
      
      // Extract error message - backend returns it in 'error' field
      let errorMsg = "Registration failed. Please try again.";
      
      if (data.error) {
        errorMsg = data.error;
      } else if (data.msg) {
        errorMsg = data.msg;
      } else if (data.message) {
        errorMsg = data.message;
      } else if (data.detail) {
        errorMsg = data.detail;
      }

      console.log("Displaying error:", errorMsg); 

      setSnackbarMessage(errorMsg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsLoading(false);
      //return false;
      // Throw error to prevent form from continuing
      throw new Error(errorMsg);
    }
    // Success case
    console.log("=== SUCCESS RESPONSE ===");
    const successMsg = data.msg || data.message || "OTP sent successfully! Please check your email.";
    console.log("Success message:", successMsg);
    
    setSnackbarMessage(successMsg);
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setIsOtpSent(true);
    setSavedEmail(email);
    
    // Clear form fields
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setUser_phone("");
    setOrganization_name("");
    setAddress("");
    setContact_email("");
    setPhone("");
    
    setIsLoading(false);
    return true;
  } catch (err) {
    console.log("=== CATCH BLOCK ===");
    console.error("Error:", err);

    // Only show error if Snackbar isn't already showing one
    if (!snackbarOpen) {
    
    let errorMsg = "Registration failed. Please try again.";

    if (err.message && err.message.includes("Failed to fetch")) {
      errorMsg = "Unable to connect to server. Please check your internet connection.";
    } else if (err.message) {
      errorMsg = err.message;
    }
    
    setSnackbarMessage(errorMsg);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  }
    setIsLoading(false);
    throw err; // Re-throw to let form handler know there was an error
    //return false;
  } 
};

  // Form submission handler using Formik
  const handleFormSubmit = async (values, { setSubmitting, setFieldError }) => {
  try {
    await handleSendOtp(values);
  } catch (err) {
    console.error("Form submission error:", err);
  } finally {
    setSubmitting(false);
  }
};

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    //setError(null);
    //setSuccess("");
    setIsVerifying(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/auth/verify-otp?email=${savedEmail}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ otp }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
  const errorMsg = data.msg || "OTP verification failed. Please check your OTP and try again.";
  //setError(errorMsg);
  setSnackbarMessage(errorMsg);
  setSnackbarSeverity("error");
  setSnackbarOpen(true);
} else {
  //setSuccess(data.msg);
  setSnackbarMessage(data.msg || "Account verified successfully!");
  setSnackbarSeverity("success");
  setSnackbarOpen(true);
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      }
    } catch (err) {
      console.error("OTP verification error:", err);
  const errorMsg = "Network error. Please try again later.";
  //setError(errorMsg);
  setSnackbarMessage(errorMsg);
  setSnackbarSeverity("error");
  setSnackbarOpen(true);
}
    finally {
      setIsVerifying(false);
    }
  };

  return (
    <Box sx={{ position: "relative", display: 'flex', justifyContent: "center", minHeight: "100vh" }}>
      {/* Left Section: Fixed Background Image (Desktop Only) */}
      {!isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: "50%",
            backgroundImage: `url(${cloudImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}
      {/* Right Section: Signup Form */}
      <Box
        sx={{
          ml: !isMobile ? "50%" : 0,
          // height: "100vh",
          overflowY: "auto",
          mr: "calc(100vw - 100%)",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: "4px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          scrollbarGutter: "stable both-edges"
          // pt: isMobile ? 4 : 2, // extra padding top for mobile
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            background: "rgba(255,255,255,0.2)",
            width: isMobile ? "90%" : "500px",
            // mb: 4,
          }}
        >
          {/* <Typography variant="h4" sx={{ mb: 3, textAlign: "center" }}>
            Athrv Cloud
          </Typography> */}
          <LogoHeader />

          {/* Radio Switch for selecting user type */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Register as</FormLabel>
            <RadioGroup
              row
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
            >
              <FormControlLabel
                value="individual"
                control={<Radio />}
                label="Individual"
              />
              <FormControlLabel
                value="organization"
                control={<Radio />}
                label="Organization"
              />
            </RadioGroup>
          </FormControl>

          {!isOtpSent ? (
            <Formik
              initialValues={initialValues}
              validationSchema={signupValidationSchema}
              onSubmit={handleFormSubmit}
            >
              {({ isSubmitting, errors, touched, handleChange, values }) => (
                <Form>
                  <Field
                    label="First Name"
                    name="firstName"
                    component={FormikTextField}
                    variant="outlined"
                    fullWidth
                    value={values.firstName}
                    onChange={handleChange}
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                  />

                  <Field
                    label="Last Name"
                    name="lastName"
                    component={FormikTextField}
                    variant="outlined"
                    fullWidth
                    value={values.lastName}
                    onChange={handleChange}
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                  />

                  <Field
                    label="Email"
                    name="email"
                    component={FormikTextField}
                    variant="outlined"
                    fullWidth
                    value={values.email}
                    onChange={handleChange}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />

                  <Field
                    label="Password"
                    name="password"
                    component={FormikTextField}
                    type="password"
                    variant="outlined"
                    fullWidth
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                  />

                    {/* Phone field */}
                  <Field
                    label="Phone"
                    name="user_phone"
                    component={FormikTextField}
                    variant="outlined"
                    fullWidth
                    value={values.user_phone}
                    onChange={handleChange}
                    error={touched.user_phone && Boolean(errors.user_phone)}
                    helperText={touched.user_phone && errors.user_phone}
                  />

                    {/* Conditional Fields */}
                  {userType === "organization" && (
                    <>
                      <TextField
                        label="Organization Name"
                        name="organization_name"
                        variant="outlined"
                        fullWidth
                        value={values.organization_name}
                        onChange={handleChange}
                        error={
                          touched.organization_name &&
                          Boolean(errors.organization_name)
                        }
                        helperText={
                          touched.organization_name &&
                          errors.organization_name
                        }
                        
                      />

                      <Field
                        label="Organization Address"
                        name="address"
                        component={FormikTextField}
                        variant="outlined"
                        fullWidth
                        value={values.address}
                        onChange={handleChange}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                        sx={{ mt: 2 }}
                      />

                      <Field
                        label="Organization Contact Email"
                        name="contact_email"
                        component={FormikTextField}
                        variant="outlined"
                        fullWidth
                        value={values.contact_email}
                        onChange={handleChange}
                        error={
                          touched.contact_email &&
                          Boolean(errors.contact_email)
                        }
                        helperText={
                          touched.contact_email && errors.contact_email
                        }
                      />

                      <Field
                        label="Organization Phone"
                        name="phone"
                        component={FormikTextField}
                        variant="outlined"
                        fullWidth
                        value={values.phone}
                        onChange={handleChange}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                    </>
                  )}

                  {userType === "individual" && (
                    <Field
                      label="Address"
                      name="address"
                      variant="outlined"
                      component={FormikTextField}
                      fullWidth
                      value={values.address}
                      onChange={handleChange}
                      error={touched.address && Boolean(errors.address)}
                      helperText={touched.address && errors.address}
                    />
                  )}

                  <Box sx={{ mt: 2, mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I accept the{' '}
                          <span 
                            onClick={(e) => {
                              e.preventDefault();
                              setOpenTermsModal(true);
                            }} 
                            style={{
                              color: theme.palette.primary.main,
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                          >
                            Terms and Conditions
                          </span>
                          {' '}and{' '}
                          <span 
                            onClick={(e) => {
                              e.preventDefault();
                              setOpenPrivacyModal(true);
                            }} 
                            style={{
                              color: theme.palette.primary.main,
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                          >
                            Privacy Policy
                          </span>
                        </Typography>
                      }
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    //disabled={isVerifying}
                    disabled={isSubmitting || !termsAccepted}
                    sx={{
                      backgroundColor: "#0C1A32",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#002244",
                        color: "#fff",
                      },
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      "SIGN UP"
                    )}
                  </Button>
                </Form>
              )}
            </Formik>
          ) : (
            <form onSubmit={handleVerifyOtp}>
             
              <TextField
                label="Enter OTP"
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isVerifying}
                sx={{
                  backgroundColor: "#0C1A32",
    "&:hover": { backgroundColor: "#002244" },
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
                }}
              >
                {isVerifying ? <CircularProgress size={20} color="inherit" /> : "Verify OTP"}
              </Button>
            </form>
          )}
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            Already have an Account?{" "}
            <span
              onClick={() => navigate("/login")}
              style={{
                color: theme.palette.primary.main,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Login
            </span>
          </Typography>
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            <a
              href="https://athrvcloud.com"
              style={{
                color: theme.palette.primary.main,
                textDecoration: "underline",
              }}
            >
              Back to Home
            </a>
          </Typography>
        </Paper>
      </Box>

      {/* Snackbar for error/success messages */}
      <Snackbar
  open={snackbarOpen}
  autoHideDuration={6000}
  onClose={handleSnackbarClose}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
>
  <Alert 
    onClose={handleSnackbarClose} 
    severity={snackbarSeverity}
    variant="filled"
    sx={{ width: '100%' }}
  >
    {snackbarMessage}
  </Alert>
</Snackbar>

      {/* Terms and Conditions Modal */}

      {/* Terms and Conditions Modal */}
      <Modal
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        aria-labelledby="terms-modal-title"
        aria-describedby="terms-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="terms-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
            Terms and Conditions
          </Typography>
          <Typography id="terms-modal-description" sx={{ whiteSpace: 'pre-line' }}>
            {termsContent || "Loading terms and conditions..."}
          </Typography>
          <Button 
            onClick={() => setOpenTermsModal(false)} 
            variant="contained" 
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal
        open={openPrivacyModal}
        onClose={() => setOpenPrivacyModal(false)}
        aria-labelledby="privacy-modal-title"
        aria-describedby="privacy-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="privacy-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
            Privacy Policy
          </Typography>
          <Typography id="privacy-modal-description" sx={{ whiteSpace: 'pre-line' }}>
            {privacyContent || "Loading privacy policy..."}
          </Typography>
          <Button 
            onClick={() => setOpenPrivacyModal(false)} 
            variant="contained" 
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default SignUpPage;